import CuesheetDataHelper from 'flux/stores/cuesheet/CuesheetDataHelper';
import { EditableCellTypes } from 'flux/stores/cuesheet/CuesheetConstants';
import role from 'shared/lib/role';
import { Map } from 'immutable';

/**
 * Core cuesheet functions
 */
export default {
  /**
   * Get the format object for a cell (any type)
   *
   * @param cell
   * @param state
   * @returns {*}
   */
  getCellFormat(cell, state) {
    state = state || this.getState();

    const userId = state.get('userId'),
      itemSetId = this.findFirstItemSetId(state),
      { rowId } = cell,
      rowIndx = this.findRowPosition(itemSetId, rowId, state),
      rowPath = state.getIn(['rows', rowIndx, 'path']),
      spec = cell.cellSpec,
      columnType = spec.get('type');

    let format = Map();

    if (EditableCellTypes.Data === columnType) {
      const columnId = spec.get('value');
      const cellPath = ['rowMap'].concat(rowPath, 'cells', columnId);
      const cellData = state.getIn(cellPath);

      if (cellData && cellData.get('format')) format = cellData.get('format');
    } else if (EditableCellTypes.ItemNote === columnType) {
      const cellPath = ['rowMap'].concat(
        rowPath,
        'userSettings',
        userId,
        'note',
      );
      const cellData = state.getIn(cellPath);

      if (cellData && cellData.get('format')) format = cellData.get('format');
    } else if (EditableCellTypes.ItemTitle === columnType) {
      const formatPath = ['rowMap'].concat(rowPath).concat('titleFormat');

      if (state.hasIn(formatPath)) {
        format = state.getIn(formatPath);
      }
    } else if (EditableCellTypes.ItemDescription === columnType) {
      const formatPath = ['rowMap'].concat(rowPath).concat('descriptionFormat');

      if (state.hasIn(formatPath)) {
        format = state.getIn(formatPath);
      }
    }

    return format || Map();
  },

  findFirstItemSetId(state) {
    state = state || this.getState();
    let rowMap = state.get('rowMap');
    if (!rowMap) return;
    return state.get('rowMap').keySeq().first();
  },

  /**
   * Get the row index, given an itemSetId and a rowId
   *
   * @param itemSetId
   * @param rowId
   * @param state optional
   */
  findRowPosition(itemSetId, rowId, state) {
    state = state || this.getState();
    itemSetId = itemSetId || state.get('itemSetId');

    let rows = state.get('rows');
    //TODO: use findRowPositionInListOfRows instead of logic below
    if (!rows) return -1;

    return rows.findIndex(
      (rowSpec) =>
        rowSpec.get('path')[0] === itemSetId &&
        rowSpec.get('path')[2] === rowId,
    );
  },

  findRowPositionInListOfRows(itemSetId, rowId, rows) {
    if (!rows) return -1;

    return rows.findIndex(
      (rowSpec) =>
        rowSpec.get('path')[0] === itemSetId &&
        rowSpec.get('path')[2] === rowId,
    );
  },

  /**
   * Get the row Id given the rowIndex
   *
   * @param rowIndex
   * @param state optional
   */
  findRowId(rowIndex, state) {
    state = state || this.getState();

    return state.getIn(['rows', rowIndex, 'path'])[2];
  },

  /**
   * Delete field state where the expectation/assumption
   * is that only a single row will have the field at a given time
   *
   * Use-cases: field selection, field editing models
   *
   * NOTE: down the road we may need this to behave differently. I'm
   * thinking of multi-cell selection use-cases
   *
   */
  clearRowFieldState(field, state) {
    state = state || this.getState();

    let found = this.findOneRowWithField(field);

    if (found === -1) return state;

    return state.deleteIn(['rows', found, field]);
  },

  /**
   * Find the row index that has a field
   */
  findOneRowWithField(field) {
    if (this.getInState('rows'))
      return this.getInState('rows').findIndex((rowSpec) => rowSpec.get(field));
  },

  /**
   * Mutate single row and return state...useful for forcing refresh
   */
  touchRow(rowIndex, state) {
    state = state || this.getState();

    if (!state || !state.get('rows')) return state;

    let val = new Date().getTime();

    return state.updateIn(['rows', rowIndex], (row) => row.set('touch', val));
  },

  /**
   * Mutate multiple rows and return state...useful for forcing refresh
   */
  touchRows(rowIndexes, state) {
    state = state || this.getState();

    if (!state || !state.get('rows')) return state;

    let val = new Date().getTime();

    rowIndexes.forEach((rowIndex) => {
      state = state.updateIn(['rows', rowIndex], (row) =>
        row.set('touch', val),
      );
    });

    return state;
  },

  /**
   * Mutate all rows with a selected cell and return state...useful for forcing refresh for bulk operations
   */
  touchAllSelectedCellRows(state) {
    return this._touchCellRows('selectedCells', state);
  },

  /**
   * Mutate all rows with a selected cell and return state...useful for forcing refresh for bulk operations
   */
  touchAllPastableCellRows(state) {
    return this._touchCellRows('pastableCells', state);
  },

  /**
   * Mutate all rows with a collab cell and return state...useful for forcing refresh for bulk operations
   */
  touchAllCollabCellRows(state) {
    return this._touchCellRows('collabCells', state);
  },

  _touchCellRows(cellKey, state) {
    state = state || this.getState();

    if (!state || !state.get('rows') || !state.get(cellKey)) return state;

    let val = new Date().getTime(),
      itemSetId = state.get('rowMap').keySeq().first();

    return state.withMutations((mutableState) => {
      state.get(cellKey).forEach((cell) => {
        mutableState.updateIn(
          ['rows', this.findRowPosition(itemSetId, cell.rowId, state)],
          (row) => {
            return row.set('touch', val);
          },
        );
      });

      return mutableState;
    });
  },

  /**
   * Mutate all rows and return state...useful for forcing refresh for bulk operations
   */
  touchAllRows(state) {
    state = state || this.getState();

    if (!state || !state.get('rows')) return state;

    let val = new Date().getTime();

    return state.update('rows', (rows) =>
      rows.map((row) => row.set('touch', val)),
    );
  },

  recalculateTimeFields(state) {
    state = state || this.getState();
    state = CuesheetDataHelper.getStateWithDerivedCalculations(
      state,
      this.getInState(['show', 'autoTimeCalculation']),
      this.getInState(['show', 'startOn']),
    );
    state = CuesheetDataHelper.getStateWithTotalDurations(state);

    state = this.touchAllRows(state);

    return state;
  },

  recalculateTimeFieldsAndTrigger(state) {
    let next = this.recalculateTimeFields(state);

    this.setStateAndTrigger(next);
  },

  isEventShowCaller() {
    return role.hasEventShowCallerRole(
      this._getUser(),
      this.getInState('eventId'),
    );
  },

  isEventAdmin() {
    if (
      role.hasTeamAdminWithoutEventRole(
        this._getUser(),
        this.getInState('teamId'),
        this.getInState('eventId'),
      )
    )
      return true;
    return role.hasEventAdminRole(
      this._getUser(),
      this.getInState('teamId'),
      this.getInState('eventId'),
    );
  },

  isEventContributor() {
    return role.hasEventContributorRole(
      this._getUser(),
      this.getInState('teamId'),
      this.getInState('eventId'),
    );
  },
};
